import { useImperativeHandle, forwardRef, Suspense, lazy, useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { LashingDetails } from '../../types/calculation';

const LashingScene = lazy(() => import('./LashingScene'));

// Props for capture handle
export interface LashingCardCaptureHandle {
    capture3DViews: () => Promise<{ backwardRight: string; forwardLeft: string } | null>;
}

interface LashingCardProps {
    width?: number;
    height?: number;
    depth?: number;
    weight?: number;
    className?: string;
    theme?: 'light' | 'dark';
    cog?: {
        width: number;
        length: number;
        height: number;
    };
    lashingDetails?: LashingDetails;
    transportUnit?: string;
    breakBulkOrientation?: 'longitudinal' | 'transverse';
    targetYOffset?: number;
    captureTheme?: 'light' | 'dark'; // Theme to use during PDF capture (defaults to 'light' for PDFs)
}

const LashingCard = forwardRef<LashingCardCaptureHandle, LashingCardProps>(({
    width = 3,
    height = 1.5,
    depth = 2,
    weight = 0,
    className = "",
    theme = 'dark',
    cog,
    lashingDetails,
    transportUnit,
    breakBulkOrientation,
    captureTheme = 'light' // Default to light for PDF captures
}, ref) => {
    const { t } = useLanguage();
    // Dynamic visualization dimensions from user input
    // Note: In Three.js for this project, `width` = Length (L), `depth` = Width (W), `height` = Height (H)
    const dimensions = { width, height, depth };
    const [zoom, setZoom] = useState(1);
    const [captureZoom, setCaptureZoom] = useState<number | null>(null); // State for capture-specific zoom override
    const [viewPreset, setViewPreset] = useState<'top' | 'front' | 'side' | 'perspective'>('perspective');
    const [cameraAngle, setCameraAngle] = useState<{ azimuth: number; elevation: number } | null>(null);
    const [targetYOffset, setTargetYOffset] = useState(0); // State for camera target vertical offset
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isCapturing, setIsCapturing] = useState(false);
    const [forceCaptureTheme, setForceCaptureTheme] = useState<'light' | 'dark' | null>(null); // Force theme during capture
    const containerRef = useRef<HTMLDivElement>(null);
    
    // Effective theme: use forced capture theme if set, otherwise use regular theme
    const effectiveTheme = forceCaptureTheme || theme;

    // Sync fullscreen state with ESC/native exits
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isFull = !!document.fullscreenElement;
            setIsFullscreen(isFull);
            
            // User Request: Automatically drop zoom to 70% when entering fullscreen
            if (isFull) {
                setZoom(0.7);
            } else {
                setZoom(1.0); // Restore to 100% when exiting
            }
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const toggleFullscreen = () => {
        if (!containerRef.current) return;

        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable fullscreen: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    // Normalize dimensions for 3D display
    const normalize3DDimensions = (dims: { width: number; height: number; depth: number }) => {
        const MIN_SIZE = 0.5;
        const MAX_LENGTH = 8;
        const MAX_WIDTH = 4;
        const MAX_HEIGHT = 4;

        const clampedWidth = Math.max(dims.width, MIN_SIZE);
        const clampedDepth = Math.max(dims.depth, MIN_SIZE);
        const clampedHeight = Math.max(dims.height, MIN_SIZE);

        let refLength = clampedWidth;
        let refWidth = clampedDepth;
        let refHeight = clampedHeight;

        // If a transport unit is specified (and it's not vessel/breakbulk, which use dynamic sizes),
        // we use the transport unit's fixed dimensions for camera framing.
        // User Request: Standardize 3D graphics proportions across all transport units 
        // based on the 20' Flatrack baseline. Transport type has no visual effect on framing.
        refLength = 5.62;
        refWidth = 2.24;
        refHeight = 2.28;

        const scaleLength = MAX_LENGTH / refLength;
        const scaleWidth = MAX_WIDTH / refWidth;
        const scaleHeight = MAX_HEIGHT / refHeight;

        // Default factor (increased recently for better framing)
        let SCENE_PADDING_FACTOR = 1.12;

        // Use a single standardized padding factor (aligned with 20' Flatrack)
        SCENE_PADDING_FACTOR = 1.12 * 0.9; // 1.008

        const scale = Math.min(scaleLength, scaleWidth, scaleHeight) * SCENE_PADDING_FACTOR;

        return {
            width: clampedWidth * scale,
            height: clampedHeight * scale,
            depth: clampedDepth * scale,
            scale
        };
    };

    const normalized = normalize3DDimensions(dimensions);

    const normalizedCog = cog
        ? {
            width: cog.width * normalized.scale,
            length: cog.length * normalized.scale,
            height: cog.height * normalized.scale,
        }
        : undefined;

    const isLight = effectiveTheme === 'light';
    const sceneBgClass = isLight ? 'bg-slate-50' : 'bg-[#0D111C]';
    const valueTextClasses = isLight ? 'font-mono text-slate-900' : 'font-mono text-slate-50';

    // Expose capture method via ref
    useImperativeHandle(ref, () => ({
        capture3DViews: async () => {
            // Capture the visible canvas using DOM query
            console.log('Starting 3D view capture for PDF...');
            const canvas = document.querySelector('.lashing-card-canvas canvas') as HTMLCanvasElement;
            if (!canvas) {
                console.error('Canvas not found for 3D capture');
                return null;
            }

            try {
                // Camera angles in radians
                // Backward-Right: 135° azimuth (2.356 rad), 25° elevation (0.436 rad)
                const BACKWARD_RIGHT_ANGLE = { azimuth: (135 * Math.PI) / 180, elevation: 0.25 };
                const FORWARD_LEFT_ANGLE = { azimuth: (-45 * Math.PI) / 180, elevation: 0.25 };

                // Activate overlay to mask jarring camera transitions
                setIsCapturing(true);
                
                // Force light theme for PDF capture
                setForceCaptureTheme(captureTheme);

                // Helper to capture at a specific angle
                const captureAtAngle = (angle: { azimuth: number; elevation: number }): Promise<string> => {
                    return new Promise((resolve) => {
                        // Set the camera angle
                        setCameraAngle(angle);

                        // User Request: Shift camera down (negative targetYOffset) to see bottom better
                        // and fill the top space. 
                        // For cargo height 1.5, shifting targetY to -0.3 moves cargo UP in frame.
                        setTargetYOffset(-0.8); // Extreme offset to ensure cargo moves UP significantly
                        setCaptureZoom(1.2);    // PDF-only emphasis while retaining label safety margins

                        // Wait for the camera to update and render
                        // We increase the timeout to 250ms to ensure the scene has fully transitioned
                        // and the renderer has completed the update.
                        setTimeout(() => {
                            requestAnimationFrame(() => {
                                requestAnimationFrame(() => {
                                    // Re-query canvas from container ref
                                    const currentCanvas = containerRef.current?.querySelector('canvas');
                                    if (currentCanvas) {
                                        const dataUrl = currentCanvas.toDataURL('image/png');
                                        const base64 = dataUrl.replace(/^data:image\/png;base64,/, '');
                                        console.log(`Captured view at azimuth ${angle.azimuth.toFixed(2)}, base64 length: ${base64.length}`);
                                        resolve(base64);
                                    } else {
                                        console.error('Canvas not found during captureAtAngle timeout');
                                        resolve('');
                                    }
                                });
                            });
                        }, 400); // 400ms for safety
                    });
                };

                // Capture Backward-Right view
                const backwardRight = await captureAtAngle(BACKWARD_RIGHT_ANGLE);

                // Capture Forward-Left view
                const forwardLeft = await captureAtAngle(FORWARD_LEFT_ANGLE);

                // Reset states to normal
                setCameraAngle(null);
                setTargetYOffset(0);
                setCaptureZoom(null);
                setForceCaptureTheme(null); // Reset forced theme
                
                // Allow react to render the restored camera before removing overlay
                setTimeout(() => setIsCapturing(false), 200);

                return { backwardRight, forwardLeft };
            } catch (error) {
                console.error('Error capturing 3D views:', error);
                setCameraAngle(null); // Reset on error
                setTargetYOffset(0);
                setCaptureZoom(null);
                setForceCaptureTheme(null); // Reset forced theme
                setIsCapturing(false);
                return null;
            }
        }
    }), []);

    return (
        <div ref={containerRef} className={`rounded-xl overflow-hidden flex flex-col ${isLight ? 'bg-white border border-slate-200' : 'bg-[#0D111C] border border-white/10'} ${className} ${isFullscreen ? 'fixed inset-0 z-[9999] rounded-none' : ''}`}>
            {/* 3D Scene Container */}
            <div className={`relative w-full h-full ${sceneBgClass} lashing-card-canvas`}>
                <Suspense fallback={
                    <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                            <p className="text-sm text-gray-400">3D Sahne Yükleniyor...</p>
                        </div>
                    </div>
                }>
                    <LashingScene dimensions={normalized} cog={normalizedCog} theme={effectiveTheme} lashingDetails={lashingDetails} zoom={captureZoom !== null ? captureZoom : zoom} onZoomChange={setZoom} viewPreset={viewPreset} cameraAngle={cameraAngle} transportUnit={transportUnit} breakBulkOrientation={breakBulkOrientation} targetYOffset={targetYOffset} isFullscreen={isFullscreen} />
                </Suspense>

                {/* Capture Overlay */}
                {isCapturing && (
                    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-[#070910]/60 backdrop-blur-sm transition-all duration-300">
                        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3 animate-pulse">
                            <svg className="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <p className="text-white font-medium text-sm tracking-wide">3D Görünümler PDF İçin Hazırlanıyor</p>
                        <p className="text-white/60 text-xs mt-1">Lütfen bekleyin...</p>
                    </div>
                )}

                {/* Camera Presets Overlay (Bottom Left) */}
                <div className="absolute bottom-3 left-3 pointer-events-auto z-20">
                    <div className={`flex items-center gap-1 p-1 rounded-lg border backdrop-blur-sm ${isLight
                        ? 'bg-white/90 border-slate-200'
                        : 'bg-black/40 border-white/10'}`}>

                        <button
                            onClick={() => setViewPreset('perspective')}
                            className={`p-1.5 rounded transition-all ${viewPreset === 'perspective'
                                ? 'bg-blue-500 text-white shadow-sm'
                                : isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-400 hover:bg-white/10'}`}
                            title="Perspective View"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                            </svg>
                        </button>

                        <button
                            onClick={() => setViewPreset('top')}
                            className={`p-1.5 rounded transition-all ${viewPreset === 'top'
                                ? 'bg-blue-500 text-white shadow-sm'
                                : isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-400 hover:bg-white/10'}`}
                            title="Top View"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <rect x="4" y="4" width="16" height="16" rx="2" strokeWidth={2} />
                                <circle cx="12" cy="12" r="2" strokeWidth={2} />
                            </svg>
                        </button>

                        <button
                            onClick={() => setViewPreset('front')}
                            className={`p-1.5 rounded transition-all ${viewPreset === 'front'
                                ? 'bg-blue-500 text-white shadow-sm'
                                : isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-400 hover:bg-white/10'}`}
                            title="Front View"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18M12 3v18M5.636 5.636l12.728 12.728M5.636 18.364L18.364 5.636" />
                            </svg>
                        </button>

                        <button
                            onClick={() => setViewPreset('side')}
                            className={`p-1.5 rounded transition-all ${viewPreset === 'side'
                                ? 'bg-blue-500 text-white shadow-sm'
                                : isLight ? 'text-slate-600 hover:bg-slate-100' : 'text-slate-400 hover:bg-white/10'}`}
                            title="Side View"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Overlay Cargo Info (Top Left) */}
                <div className="absolute top-3 left-3 z-10 pointer-events-none">
                    <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2 border-b pb-1.5 mb-0.5 border-dashed border-opacity-20 border-current"
                            style={{ borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)' }}>
                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                            <span className={`text-[10px] font-bold tracking-wider uppercase ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                                {t('labels.cargo_specs') || 'CARGO'}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            <div className="flex items-center justify-between gap-2">
                                <span className={`text-[10px] font-medium ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>Weight</span>
                                <span className={`text-[11px] font-mono ${valueTextClasses}`}>{weight.toLocaleString()} MT</span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <span className={`text-[10px] font-medium ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>Height</span>
                                <span className={`text-[11px] font-mono ${valueTextClasses}`}>{height.toFixed(2)}m</span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <span className={`text-[10px] font-medium ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>Width</span>
                                <span className={`text-[11px] font-mono ${valueTextClasses}`}>{depth.toFixed(2)}m</span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <span className={`text-[10px] font-medium ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>Length</span>
                                <span className={`text-[11px] font-mono ${valueTextClasses}`}>{width.toFixed(2)}m</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Zoom Controls + Reset Button - Top Right */}
                <div className="absolute top-3 right-3 pointer-events-auto z-20">
                    <div className={`flex items-center gap-1 p-1.5 rounded-md border backdrop-blur-sm ${isLight
                        ? 'bg-white/90 border-slate-200 shadow-lg'
                        : 'bg-black/80 border-white/20 shadow-xl'}`}>
                        <button
                            onClick={() => setZoom(prev => Math.max(0.5, prev - 0.1))}
                            className={`w-6 h-6 rounded flex items-center justify-center transition-all duration-200 ${isLight
                                ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300'
                                : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                                }`}
                            title="Zoom Out"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                        </button>

                        <div className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${isLight
                            ? 'bg-slate-100 text-slate-700 border border-slate-300'
                            : 'bg-white/10 text-white border border-white/10'
                            }`}>
                            {Math.round(zoom * 100)}%
                        </div>

                        <button
                            onClick={() => setZoom(prev => Math.min(3, prev + 0.1))}
                            className={`w-6 h-6 rounded flex items-center justify-center transition-all duration-200 ${isLight
                                ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300'
                                : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                                }`}
                            title="Zoom In"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </button>

                        <div className={`w-px h-4 mx-1 ${isLight ? 'bg-slate-200' : 'bg-white/10'}`} />

                        <button
                            onClick={toggleFullscreen}
                            className={`w-6 h-6 rounded flex items-center justify-center transition-all duration-200 ${isLight
                                ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300'
                                : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                                }`}
                            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                        >
                            {isFullscreen ? (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
});

LashingCard.displayName = 'LashingCard';

export default LashingCard;
